import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LoadingAnimation from '../LoadingAnimation';
import styles from './index.module.scss';

const normalizeText = (s) => (s || '').replace(/\r\n/g, '\n');

const SingleQuestion = ({ visible, question, history = [], isWaiting = false, isFlowEnded = false, onClose, onSelect }) => {
  const [selected, setSelected] = useState('');

  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (visible) setSelected('');
  }, [visible, question?.id]);

  const disabled = isWaiting || isFlowEnded || !question;

  const showWaiting = Boolean(isWaiting && !isFlowEnded);
  const showEnded = Boolean(isFlowEnded);


  const renderPrefixBadge = (prefix) => {
    const p = String(prefix || '').trim();
    if (!p) return null;
    return <div className={styles.prefixBadge}>{`对于${p}流程的问题`}</div>;
  };

  const title = useMemo(() => {
    if (!question) return '';
    const qid = question.id ? String(question.id).replace(/^Q/i, 'Q') : '';
    return qid ? `${qid}. ${question.title || ''}` : (question.title || '');
  }, [question]);

  // 检测是否为移动设备
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // 重置位置
  useEffect(() => {
    if (!isMobile && visible) {
      setPosition(null);
    }
  }, [visible, isMobile]);

  // 处理鼠标按下事件
  const handleMouseDown = useCallback((e) => {
    if (isMobile) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    
    // 如果还没有位置，设置初始位置
    if (!position) {
      setPosition({
        x: rect.left,
        y: rect.top
      });
    }
    
    // 计算鼠标位置与容器左上角的偏移量
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    
    setIsDragging(true);
    setDragOffset({ x: offsetX, y: offsetY });
  }, [isMobile, position]);

  // 处理鼠标移动事件
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || isMobile) return;
    
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    
    // 计算新位置
    let newX = e.clientX - dragOffset.x;
    let newY = e.clientY - dragOffset.y;
    
    // 限制在视口范围内
    newX = Math.max(0, Math.min(window.innerWidth - containerRect.width, newX));
    newY = Math.max(0, Math.min(window.innerHeight - containerRect.height, newY));
    
    setPosition({ x: newX, y: newY });
  }, [isDragging, dragOffset, isMobile]);

  // 处理鼠标抬起事件
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 添加/移除全局事件监听
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!visible) return null;

  const renderHistoryItem = (item, idx) => {
    const qid = item?.id ? String(item.id).replace(/^Q/i, 'Q') : '';
    const historyTitle = qid ? `${qid}. ${item.title || ''}` : (item.title || '');

    const selectedLetter = String(item.selectedOption || '').trim();
    const selectedOptionText = String(item.selectedOptionText || '').trim();

    return (
      <div key={item.historyId || `${qid}_${idx}`} className={styles.historyItem}>
        {renderPrefixBadge(item.prefix)}
        <div className={styles.historyTitle} title={historyTitle}>{historyTitle}</div>
        <div className={styles.historyAnswerLine}>
          <span className={styles.historyAnswerLabel}>已选：</span>
          <span className={styles.historyAnswerValue}>{selectedLetter || '-'}</span>
        </div>
        <div className={styles.historyAnswerText}>
          {selectedOptionText || '-'}
        </div>
      </div>
    );
  };

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
    >
      <div
        ref={containerRef}
        className={`${styles.container} ${isDragging ? styles.dragging : ''}`}
        style={position ? { left: `${position.x}px`, top: `${position.y}px` } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={styles.header}
          onMouseDown={handleMouseDown}
        >
          <div className={styles.headerLeft}>
            <div className={styles.badge}>问题</div>
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label="关闭">×</button>
        </div>

        <div className={styles.content}>
          {history.length > 0 && (
            <div className={styles.historySection}>
              <div className={styles.historySectionTitle}>历史问题</div>
              <div className={styles.historyList}>
                {history.map(renderHistoryItem)}
              </div>
            </div>
          )}

          <div className={styles.currentSection}>
            <div className={styles.currentSectionTitle}>当前问题</div>

            {showWaiting && (
              <div className={styles.waitingBox}>
                <LoadingAnimation
                  message="AI正在为您生成专属制备流程方案..."
                  subMessage="请稍候"
                  type="questionnaire"
                />
              </div>
            )}

            {showEnded && (
              <div className={styles.endedBox}>
                <div className={styles.endedTitle}>流程已结束</div>
                <div className={styles.endedSub}>可在上方“历史问题”中查看本次选择记录</div>
              </div>
            )}

            {!showWaiting && !showEnded && question && (
              <>
                {renderPrefixBadge(question?.prefix)}
        <div className={styles.questionTitle} title={title}>{title}</div>

          <div className={styles.options}>
            {question.options.map((opt) => (
              <button
                key={opt.letter}
                      className={`${styles.optionButton} ${selected === opt.letter ? styles.selected : ''} ${disabled ? styles.disabledOption : ''}`}
                      onClick={() => {
                        if (disabled) return;
                        setSelected(opt.letter);
                      }}
                      disabled={disabled}
              >
                <div className={styles.optionLetter}>{opt.letter}</div>
                <div className={styles.optionText}>{normalizeText(opt.text)}</div>
              </button>
            ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelButton} onClick={onClose}>取消</button>
          {!showEnded && (
            <button
              className={`${styles.submitButton} ${(!selected || disabled) ? styles.disabled : ''}`}
              disabled={!selected || disabled}
              onClick={() => {
                if (disabled) return;
                onSelect?.(selected);
              }}
            >
              确认并发送
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleQuestion;

